import { httpMethod } from "@/types/http"
import { getUTMParamsFromLocalStorage } from "../utils";
import { BookingDetails } from "@/types/booking";
import { sendGTMEvent } from "@next/third-parties/google";
import { reportGoogleAdsLead } from "@/lib/analytics/google-ads";

export async function bookMeeting(bookingDetails: BookingDetails, component: string) {
  const utmData = getUTMParamsFromLocalStorage();
  const fullPayload = {
    ...bookingDetails,
    ...utmData,
  };

  const response = await fetch("/api/calendar", {
    method: httpMethod.POST,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(fullPayload),
  });

  // The conversion event used to fire before the request went out, so Meta and
  // Google Ads counted every failed or abandoned attempt as a booking. Only a
  // confirmed booking is a conversion.
  if (response.ok) {
    sendGTMEvent({
      event: "conversion",
      eventData: {
        send_to: 'AW-16923196898/BO6wCOXM-68aEOL7zYU_',
        value: 1.0,
        currency: 'ILS',
        component: component,
      },
    });
    reportGoogleAdsLead();
  }

  return response;
}

// ── Availability, fetched once and shared ───────────────────────────────────
// The booking calendar asks for a date's free slots on mount and on every date
// the lead clicks. Each ask was a fresh round trip (~3s before the server-side
// cache, and still a round trip after it), so the lead sat on a spinner at a
// moment when we already knew which date they'd land on.
//
// Two fixes live here. Requests for the same date are shared rather than
// duplicated (`inFlight`), and answers are reused for a minute (`cached`,
// matching the server's TTL). That makes `prefetchAvailableTimes` possible:
// start the fetch while the lead is still filling in the form, and by the time
// the calendar renders the slots are already in hand.
const AVAILABILITY_TTL_MS = 60_000;
const cached = new Map<string, { slots: string[]; expiresAt: number }>();
const inFlight = new Map<string, Promise<string[]>>();

/** The date the booking calendar opens on - tomorrow. Prefetch targets this. */
export function defaultBookingDate(): Date {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  return tomorrow;
}

/** `yyyy-MM-dd` in the viewer's own timezone - matches what the calendar sends. */
export function toDateKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Free slots for a date. THROWS when the lookup fails - a failed lookup used
 * to be returned as `[]`, which the UI rendered as "no availability": a dead
 * Google token looked exactly like a fully-booked week and silently zeroed out
 * bookings. Callers must treat the throw as "availability unknown", never as
 * "no slots".
 */
export async function getAvailableTimes(date: string, timeZone: string): Promise<string[]> {
  const key = `${date}|${timeZone}`;

  const hit = cached.get(key);
  if (hit && hit.expiresAt > Date.now()) return hit.slots;

  const existing = inFlight.get(key);
  if (existing) return existing;

  const request = (async () => {
    try {
      const response = await fetch(`/api/calendar?date=${date}&timeZone=${timeZone}`);
      if (!response.ok) throw new Error(`availability request failed: ${response.status}`);
      const slots = await response.json();
      if (!Array.isArray(slots)) throw new Error("availability request returned a non-array");
      cached.set(key, { slots, expiresAt: Date.now() + AVAILABILITY_TTL_MS });
      return slots;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);
  return request;
}

/**
 * Warm the cache for a date the lead hasn't asked for yet. Fire-and-forget:
 * failures are the normal fetch path's problem, not the caller's.
 */
export function prefetchAvailableTimes(date: string, timeZone: string): void {
  void getAvailableTimes(date, timeZone).catch(() => {});
}

/** Drop a date's cached slots - used when a slot turns out to be taken. */
export function invalidateAvailableTimes(date: string, timeZone: string): void {
  cached.delete(`${date}|${timeZone}`);
}

export async function getNextAvailableDate(date: string, timeZone: string) {
  const response = await fetch(`/api/calendar/next-available?date=${date}&timeZone=${timeZone}`);
  if (!response.ok) {
    return [];
  }
  return await response.json();
}
  
  